import { formatRelativeTime } from "@/lib/format";
import type { PlatformPulse } from "@/types";

export interface PlatformStatusSummary {
  headline: string;
  lines: string[];
}

/** Rule-based summary from Platform Pulse. Replace with `aiSummary` when available. */
export function buildPlatformSummary(pulse: PlatformPulse): PlatformStatusSummary {
  const headline =
    pulse.status === "healthy"
      ? "Platform Healthy"
      : pulse.status === "degraded"
        ? "Platform Degraded"
        : "Platform Critical";

  const fail = pulse.doctorSummary?.fail ?? 0;
  const warn = pulse.doctorSummary?.warn ?? 0;
  const scanned = pulse.tenantsDoctorScanned ?? pulse.totalTenants;
  const notScanned = Math.max(0, pulse.totalTenants - scanned);

  const lines: string[] = [];

  if (fail > 0) {
    lines.push(
      `${fail} tenant${fail === 1 ? "" : "s"} with open issues.`,
    );
  } else if (warn > 0) {
    lines.push(
      `${warn} tenant${warn === 1 ? "" : "s"} on watch.`,
    );
  } else if (notScanned > 0) {
    lines.push(
      `${notScanned} tenant${notScanned === 1 ? " has" : "s have"} not been verified today.`,
    );
  } else if (pulse.runtimeDoctorVerdict === "healthy") {
    lines.push("All scanned tenants are healthy.");
  } else {
    lines.push("Platform verification incomplete or inconclusive.");
  }

  if (pulse.status === "critical") {
    lines.push("Customer impact possible — do not assume traffic is unaffected.");
  } else if (pulse.status === "degraded" && fail > 0) {
    lines.push("Customer impact unknown until failing tenants are reviewed.");
  } else if (pulse.activeTenants > 0) {
    lines.push("No critical customer-impact signal in the current pulse.");
  } else {
    lines.push("No active tenant stacks reported.");
  }

  lines.push(`Last verified ${formatRelativeTime(pulse.lastChecked)}.`);

  return { headline, lines };
}
